import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { MessageCircle, ArrowUpRight } from "lucide-react";
import { CONTACT } from "@/lib/siteData";

const ease = [0.22, 1, 0.36, 1];

const Line = ({ children, delay, className = "" }) => (
  <span className="block overflow-hidden pb-[0.12em]">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "115%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.05, ease, delay }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.08, 1.22]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);

  return (
    <section ref={ref} id="top" data-testid="hero-section" className="relative min-h-[100svh] flex flex-col justify-end overflow-hidden">
      {/* parallax background */}
      <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0 z-0">
        <img
          src="https://images.unsplash.com/photo-1445754574409-bcd715e18017?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000"
          alt="Court architecture"
          className="w-full h-full object-cover grayscale-img opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#060606] via-[#060606]/70 to-[#060606]/30" />
      </motion.div>

      {/* top meta bar */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.6 }}
        className="absolute top-24 md:top-28 left-0 right-0 z-10 px-6 md:px-12"
      >
        <div className="mx-auto max-w-[1500px] flex justify-between font-mono-plex text-[10px] md:text-[11px] uppercase tracking-[0.22em] text-[var(--muted)]">
          <span>Est. Practice · {CONTACT.experience} Years</span>
          <span className="hidden sm:block">{CONTACT.court}</span>
          <span>26.09° N · Purnia</span>
        </div>
      </motion.div>

      {/* headline */}
      <motion.div style={{ y: textY }} className="relative z-10 px-6 md:px-12 pb-16 md:pb-24">
        <div className="mx-auto max-w-[1500px]">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="font-mono-plex text-[11px] uppercase tracking-[0.3em] text-[var(--muted)] mb-6 md:mb-10"
          >
            {CONTACT.credentials} — Advocate
          </motion.p>

          <h1 className="font-serif-display font-light leading-[0.94] tracking-tight text-[15vw] md:text-[12vw] lg:text-[10.5vw]">
            <Line delay={0.35}>Sanjay Kr</Line>
            <Line delay={0.47} className="italic pl-[0.08em]">Sinha</Line>
          </h1>

          <div className="mt-8 md:mt-12 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
            <motion.p
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease, delay: 0.9 }}
              className="max-w-md text-base md:text-lg text-[var(--off)]/85 leading-relaxed"
            >
              {CONTACT.role} in {CONTACT.location}. Nearly three decades of counsel in property, land and revenue matters before the courts of Purnia.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease, delay: 1.05 }}
              className="flex flex-wrap items-center gap-4"
            >
              <a
                href="#consult"
                data-testid="hero-book-btn"
                className="group flex items-center gap-3 bg-[var(--off)] text-[#060606] pl-6 pr-4 py-4 font-mono-plex text-xs uppercase tracking-[0.18em] hover:bg-white transition-colors duration-300"
              >
                Book a Consultation
                <span className="grid place-items-center h-7 w-7 rounded-full bg-[#060606] text-[var(--off)] transition-transform duration-300 group-hover:rotate-45">
                  <ArrowUpRight size={15} />
                </span>
              </a>
              <a
                href={`https://wa.me/${CONTACT.whatsapp}`}
                target="_blank"
                rel="noreferrer"
                data-testid="hero-whatsapp-btn"
                className="flex items-center gap-2 border border-[var(--line)] px-6 py-4 font-mono-plex text-xs uppercase tracking-[0.18em] text-[var(--off)] hover:bg-[var(--off)]/5 transition-colors duration-300"
              >
                <MessageCircle size={15} /> Call / WhatsApp
              </a>
            </motion.div>
          </div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="relative z-10 border-t border-[var(--line)]"
      >
        <div className="mx-auto max-w-[1500px] px-6 md:px-12 py-4 flex items-center justify-between font-mono-plex text-[10px] uppercase tracking-[0.22em] text-[var(--muted)]">
          <span>Civil · Revenue Litigation</span>
          <span className="animate-pulse">Scroll ↓</span>
        </div>
      </motion.div>
    </section>
  );
};
